import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Pencil, Trash2, ChevronDown, ChevronRight,
  Building2, Briefcase, Layers, Lightbulb, X,
} from "lucide-react";

export default function AdminIndustries() {
  const [industries, setIndustries] = useState([]);
  const [families, setFamilies] = useState({});   // { industry_id: [families] }
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Industry dialogs
  const [indCreateOpen, setIndCreateOpen] = useState(false);
  const [indCreateName, setIndCreateName] = useState("");
  const [indCreateDesc, setIndCreateDesc] = useState("");
  const [indEditTarget, setIndEditTarget] = useState(null);
  const [indEditName, setIndEditName] = useState("");
  const [indEditDesc, setIndEditDesc] = useState("");
  const [indDeleteTarget, setIndDeleteTarget] = useState(null);

  // Family dialogs
  const [famCreateTarget, setFamCreateTarget] = useState(null); // industry_id
  const [famCreateName, setFamCreateName] = useState("");
  const [famCreateDesc, setFamCreateDesc] = useState("");
  const [famEditTarget, setFamEditTarget] = useState(null);
  const [famEditName, setFamEditName] = useState("");
  const [famEditDesc, setFamEditDesc] = useState("");
  const [famDeleteTarget, setFamDeleteTarget] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [indRes, sugRes] = await Promise.all([
        api.get("/admin/industries"),
        api.get("/admin/industry-suggestions"),
      ]);
      setIndustries(indRes.data);
      setSuggestions(sugRes.data);
    } catch { toast.error("Could not load industries"); }
    finally { setLoading(false); }
  };

  const dismissSuggestion = async (name) => {
    try {
      await api.delete(`/admin/industry-suggestions/${encodeURIComponent(name)}`);
      setSuggestions((prev) => prev.filter((s) => s.name.toLowerCase() !== name.toLowerCase()));
    } catch { toast.error("Could not dismiss"); }
  };

  const promoteToIndustry = (name) => {
    setIndCreateName(name);
    setIndCreateDesc("");
    setIndCreateOpen(true);
    dismissSuggestion(name);
  };

  const fetchFamilies = async (industry_id) => {
    try {
      const { data } = await api.get(`/admin/industries/${industry_id}/families`);
      setFamilies((prev) => ({ ...prev, [industry_id]: data }));
    } catch {}
  };

  useEffect(() => { fetchAll(); }, []);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) { n.delete(id); }
      else { n.add(id); fetchFamilies(id); }
      return n;
    });
  };

  // ── Industry CRUD ─────────────────────────────────────────────────────────
  const createIndustry = async () => {
    if (!indCreateName.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/industries", { name: indCreateName.trim(), description: indCreateDesc.trim() });
      setIndustries((prev) => [...prev, { ...data, family_count: 0, role_count: 0 }]);
      setIndCreateOpen(false); setIndCreateName(""); setIndCreateDesc("");
      toast.success(`Industry "${data.name}" created`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setBusy(false); }
  };

  const saveIndustry = async () => {
    if (!indEditName.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/industries/${indEditTarget.industry_id}`, { name: indEditName.trim(), description: indEditDesc.trim() });
      setIndustries((prev) => prev.map((i) => i.industry_id === indEditTarget.industry_id ? { ...i, name: indEditName.trim(), description: indEditDesc.trim() } : i));
      setIndEditTarget(null);
      toast.success("Industry updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };

  const toggleIndustryActive = async (ind) => {
    try {
      await api.patch(`/admin/industries/${ind.industry_id}`, { is_active: !ind.is_active });
      setIndustries((prev) => prev.map((i) => i.industry_id === ind.industry_id ? { ...i, is_active: !ind.is_active } : i));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const deleteIndustry = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/industries/${indDeleteTarget.industry_id}`);
      setIndustries((prev) => prev.filter((i) => i.industry_id !== indDeleteTarget.industry_id));
      setIndDeleteTarget(null);
      toast.success("Industry deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    finally { setBusy(false); }
  };

  // ── Family CRUD ───────────────────────────────────────────────────────────
  const createFamily = async () => {
    if (!famCreateName.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/industries/${famCreateTarget}/families`, { name: famCreateName.trim(), description: famCreateDesc.trim() });
      setFamilies((prev) => ({ ...prev, [famCreateTarget]: [...(prev[famCreateTarget] || []), { ...data, role_count: 0 }] }));
      setIndustries((prev) => prev.map((i) => i.industry_id === famCreateTarget ? { ...i, family_count: (i.family_count || 0) + 1 } : i));
      setFamCreateTarget(null); setFamCreateName(""); setFamCreateDesc("");
      toast.success(`Job family "${data.name}" created`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setBusy(false); }
  };

  const saveFamily = async () => {
    if (!famEditName.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/families/${famEditTarget.family_id}`, { name: famEditName.trim(), description: famEditDesc.trim() });
      setFamilies((prev) => {
        const iid = famEditTarget.industry_id;
        return { ...prev, [iid]: (prev[iid] || []).map((f) => f.family_id === famEditTarget.family_id ? { ...f, name: famEditName.trim(), description: famEditDesc.trim() } : f) };
      });
      setFamEditTarget(null);
      toast.success("Job family updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };

  const deleteFamily = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/families/${famDeleteTarget.family_id}`);
      setFamilies((prev) => {
        const iid = famDeleteTarget.industry_id;
        return { ...prev, [iid]: (prev[iid] || []).filter((f) => f.family_id !== famDeleteTarget.family_id) };
      });
      setIndustries((prev) => prev.map((i) => i.industry_id === famDeleteTarget.industry_id ? { ...i, family_count: Math.max(0, (i.family_count || 1) - 1) } : i));
      setFamDeleteTarget(null);
      toast.success("Job family deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    finally { setBusy(false); }
  };

  return (
    <main className="max-w-5xl mx-auto px-6 md:px-12 py-12">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <Link to="/admin" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-[#0025D8] inline-flex items-center gap-1.5 transition-colors group">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" /> Admin
          </Link>
          <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2">Industries & Job Families</h1>
          <p className="text-muted-foreground mt-1">Organise roles into industries and job families so candidates can navigate easily.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/roles">
            <Button variant="outline" className="rounded-xl h-12 px-5 text-sm">
              <Layers className="w-4 h-4 mr-2" /> Manage Roles
            </Button>
          </Link>
          <Button onClick={() => setIndCreateOpen(true)} className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF] h-12 px-5">
            <Plus className="w-4 h-4 mr-2" /> New Industry
          </Button>
        </div>
      </div>

      {/* Suggested by companies */}
      {suggestions.length > 0 && (
        <div className="mb-8 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/5">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-[#F59E0B]/30">
            <Lightbulb className="w-4 h-4 text-[#F59E0B]" />
            <span className="font-mono text-[11px] tracking-[0.2em] text-[#F59E0B] font-semibold">
              SUGGESTED BY COMPANIES
            </span>
            <span className="ml-auto font-mono text-[10px] text-[#F59E0B]/70">{suggestions.length} suggestion{suggestions.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="divide-y divide-[#F59E0B]/20">
            {suggestions.map((s) => (
              <div key={s.name} className="flex items-center gap-4 px-5 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{s.name}</span>
                    <span className="font-mono text-[10px] px-1.5 py-0.5 bg-[#F59E0B]/20 text-[#B45309] rounded-lg border border-[#F59E0B]/40">
                      ×{s.count} {s.count === 1 ? "request" : "requests"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    From: {s.companies.slice(0, 3).join(", ")}{s.companies.length > 3 ? ` +${s.companies.length - 3} more` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => promoteToIndustry(s.name)}
                    className="rounded-xl h-8 px-3 text-xs bg-[#0025D8] hover:bg-[#001CB3]">
                    <Plus className="w-3 h-3 mr-1" /> Add as Industry
                  </Button>
                  <button
                    onClick={() => dismissSuggestion(s.name)}
                    className="text-muted-foreground hover:text-[#E11D48] transition-colors"
                    title="Dismiss">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Industry list */}
      {loading ? (
        <div className="p-12 font-mono text-sm text-muted-foreground">Loading…</div>
      ) : industries.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground">
          <Building2 className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="font-semibold">No industries yet.</p>
          <p className="text-sm mt-1">Create an industry (e.g. "IT", "Medical") then add job families inside it.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {industries.map((ind) => {
            const open = expanded.has(ind.industry_id);
            const fams = families[ind.industry_id] || [];
            return (
              <div key={ind.industry_id} className="rounded-xl border border-border">
                {/* Industry row */}
                <div className="flex items-center gap-3 px-4 py-4 bg-secondary/30">
                  <button onClick={() => toggleExpand(ind.industry_id)} className="text-muted-foreground hover:text-foreground flex-shrink-0">
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <Building2 className="w-4 h-4 text-[#1D4ED8] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{ind.name}</span>
                      {!ind.is_active && <span className="font-mono text-[9px] px-1.5 py-0.5 bg-secondary text-muted-foreground rounded-xl border border-border uppercase">Inactive</span>}
                    </div>
                    {ind.description && <div className="text-xs text-muted-foreground truncate">{ind.description}</div>}
                    <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                      {ind.family_count || 0} job {ind.family_count === 1 ? "family" : "families"} · {ind.role_count || 0} roles
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch checked={ind.is_active} onCheckedChange={() => toggleIndustryActive(ind)} />
                    <Button variant="outline" size="sm" className="rounded-xl h-8 px-2 text-xs"
                      onClick={() => { setIndEditTarget(ind); setIndEditName(ind.name); setIndEditDesc(ind.description || ""); }}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-xl h-8 px-2 text-xs hover:bg-[#E11D48] hover:text-white hover:border-[#E11D48]"
                      onClick={() => setIndDeleteTarget(ind)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-xl h-8 px-3 text-xs"
                      onClick={() => { setFamCreateTarget(ind.industry_id); setFamCreateName(""); setFamCreateDesc(""); if (!open) toggleExpand(ind.industry_id); }}>
                      <Plus className="w-3 h-3 mr-1" /> Add Family
                    </Button>
                  </div>
                </div>

                {/* Job families */}
                {open && (
                  <div className="border-t border-border">
                    {fams.length === 0 ? (
                      <div className="px-12 py-4 text-sm text-muted-foreground">
                        No job families yet. Click "Add Family" to create one (e.g. "Quality Assurance", "Frontend Development").
                      </div>
                    ) : (
                      fams.map((fam) => (
                        <div key={fam.family_id} className="flex items-center gap-3 px-12 py-3 border-b border-border last:border-b-0 hover:bg-secondary/20">
                          <Briefcase className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{fam.name}</div>
                            {fam.description && <div className="text-xs text-muted-foreground truncate">{fam.description}</div>}
                            <div className="font-mono text-[10px] text-muted-foreground">{fam.role_count || 0} roles</div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button variant="outline" size="sm" className="rounded-xl h-7 px-2 text-xs"
                              onClick={() => { setFamEditTarget(fam); setFamEditName(fam.name); setFamEditDesc(fam.description || ""); }}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button variant="outline" size="sm" className="rounded-xl h-7 px-2 text-xs hover:bg-[#E11D48] hover:text-white hover:border-[#E11D48]"
                              onClick={() => setFamDeleteTarget(fam)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Industry dialogs ── */}
      <Dialog open={indCreateOpen} onOpenChange={(o) => { if (!o) { setIndCreateOpen(false); setIndCreateName(""); setIndCreateDesc(""); } }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">New industry</DialogTitle>
            <DialogDescription>e.g. "Information Technology", "Healthcare", "Food & Manufacturing"</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Industry name</Label>
              <Input autoFocus value={indCreateName} onChange={(e) => setIndCreateName(e.target.value)} placeholder="Information Technology" className="rounded-xl py-6" /></div>
            <div><Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Description <span className="normal-case font-normal text-muted-foreground">(optional)</span></Label>
              <Input value={indCreateDesc} onChange={(e) => setIndCreateDesc(e.target.value)} placeholder="Short description shown to candidates" className="rounded-xl py-6" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setIndCreateOpen(false)}>Cancel</Button>
            <Button onClick={createIndustry} disabled={busy || !indCreateName.trim()} className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]">
              <Plus className="w-4 h-4 mr-2" />{busy ? "Creating…" : "Create industry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!indEditTarget} onOpenChange={(o) => { if (!o) setIndEditTarget(null); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader><DialogTitle className="font-display font-bold">Edit industry</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Industry name</Label>
              <Input autoFocus value={indEditName} onChange={(e) => setIndEditName(e.target.value)} className="rounded-xl py-6" /></div>
            <div><Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Description</Label>
              <Input value={indEditDesc} onChange={(e) => setIndEditDesc(e.target.value)} className="rounded-xl py-6" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setIndEditTarget(null)}>Cancel</Button>
            <Button onClick={saveIndustry} disabled={busy || !indEditName.trim()} className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]">{busy ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!indDeleteTarget} onOpenChange={(o) => { if (!o) setIndDeleteTarget(null); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-[#E11D48]">Delete industry</DialogTitle>
            <DialogDescription>Delete <strong>{indDeleteTarget?.name}</strong>? All job families inside it must be removed first.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setIndDeleteTarget(null)}>Cancel</Button>
            <Button onClick={deleteIndustry} disabled={busy} className="rounded-xl bg-[#E11D48] hover:bg-[#BE123C]">
              <Trash2 className="w-4 h-4 mr-2" />{busy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Family dialogs ── */}
      <Dialog open={!!famCreateTarget} onOpenChange={(o) => { if (!o) { setFamCreateTarget(null); setFamCreateName(""); setFamCreateDesc(""); } }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">New job family</DialogTitle>
            <DialogDescription>e.g. "Quality Assurance", "Frontend Development", "Data Engineering"</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Family name</Label>
              <Input autoFocus value={famCreateName} onChange={(e) => setFamCreateName(e.target.value)} placeholder="Quality Assurance" className="rounded-xl py-6" /></div>
            <div><Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Description <span className="normal-case font-normal text-muted-foreground">(optional)</span></Label>
              <Input value={famCreateDesc} onChange={(e) => setFamCreateDesc(e.target.value)} placeholder="Short description" className="rounded-xl py-6" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setFamCreateTarget(null)}>Cancel</Button>
            <Button onClick={createFamily} disabled={busy || !famCreateName.trim()} className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]">
              <Plus className="w-4 h-4 mr-2" />{busy ? "Creating…" : "Create job family"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!famEditTarget} onOpenChange={(o) => { if (!o) setFamEditTarget(null); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader><DialogTitle className="font-display font-bold">Edit job family</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Family name</Label>
              <Input autoFocus value={famEditName} onChange={(e) => setFamEditName(e.target.value)} className="rounded-xl py-6" /></div>
            <div><Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Description</Label>
              <Input value={famEditDesc} onChange={(e) => setFamEditDesc(e.target.value)} className="rounded-xl py-6" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setFamEditTarget(null)}>Cancel</Button>
            <Button onClick={saveFamily} disabled={busy || !famEditName.trim()} className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]">{busy ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!famDeleteTarget} onOpenChange={(o) => { if (!o) setFamDeleteTarget(null); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-[#E11D48]">Delete job family</DialogTitle>
            <DialogDescription>Delete <strong>{famDeleteTarget?.name}</strong>? All roles in this family must be moved or deleted first.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setFamDeleteTarget(null)}>Cancel</Button>
            <Button onClick={deleteFamily} disabled={busy} className="rounded-xl bg-[#E11D48] hover:bg-[#BE123C]">
              <Trash2 className="w-4 h-4 mr-2" />{busy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
